import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InviteDto } from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { Badge, Button, Card, Spinner, Toast } from '../../components/ui';
import { useT } from '../../i18n';
import { api } from '../../lib/api';
import { creditsCount, parseSettingValue, personLabel } from '../../lib/format';
import { haptic } from '../../lib/tma';

const inputClass =
  'w-full rounded-lg px-4 py-2.5 text-sm placeholder:text-hint bg-surface-2 border-0 focus:outline-none focus:ring-2 focus:ring-ink/70';

export function AdminInvitesPage() {
  const t = useT();
  const qc = useQueryClient();
  const [credits, setCredits] = useState('300');
  const [note, setNote] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const flash = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 2500);
  };

  const list = useQuery({
    queryKey: ['admin', 'invites'],
    queryFn: () => api.adminListInvites(),
  });

  const create = useMutation({
    mutationFn: (input: { credits: number; note?: string }) => api.adminCreateInvite(input),
    onSuccess: () => {
      setNote('');
      haptic('success');
      qc.invalidateQueries({ queryKey: ['admin', 'invites'] });
    },
    onError: () => flash(t.common.failed),
  });

  const revoke = useMutation({
    mutationFn: (id: number) => api.adminRevokeInvite(id),
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'invites'] }),
    onError: () => flash(t.common.failed),
  });

  const parsedCredits = parseSettingValue(credits);

  function copy(invite: InviteDto) {
    if (!invite.link) return;
    navigator.clipboard
      .writeText(invite.link)
      .then(() => {
        haptic('success');
        flash(t.common.copied);
      })
      .catch(() => flash(t.common.failed));
  }

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <PageHeader title={t.admin.invites} to="/admin" />

      <Card variant="surface" padding="md" className="mb-4 space-y-2">
        <label className="block text-xs text-hint" htmlFor="invite-credits">
          {t.admin.inviteCredits}
        </label>
        <input
          id="invite-credits"
          inputMode="numeric"
          value={credits}
          onChange={(e) => setCredits(e.target.value)}
          className={inputClass}
        />
        <label className="block text-xs text-hint" htmlFor="invite-note">
          {t.admin.inviteNote}
        </label>
        <input
          id="invite-note"
          value={note}
          maxLength={200}
          onChange={(e) => setNote(e.target.value)}
          className={inputClass}
        />
        {parsedCredits === null && <p className="text-xs text-destructive">{t.admin.settingsBad}</p>}
        <Button
          variant="ink"
          fullWidth
          loading={create.isPending}
          disabled={parsedCredits === null || parsedCredits > 100_000}
          onClick={() => {
            if (parsedCredits === null) return;
            const trimmed = note.trim();
            create.mutate({ credits: parsedCredits, ...(trimmed ? { note: trimmed } : {}) });
          }}
        >
          {t.admin.inviteCreate}
        </Button>
      </Card>

      {list.isLoading && (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      )}
      {list.isError && <p className="text-sm text-hint">{t.common.failed}</p>}
      {list.data && list.data.invites.length === 0 && (
        <p className="text-sm text-hint">{t.admin.inviteEmpty}</p>
      )}

      <div className="space-y-2">
        {list.data?.invites.map((inv) => (
          <Card key={inv.id} variant="surface" padding="md">
            <div className="flex items-center justify-between gap-3 mb-1">
              <span className="font-mono text-sm">{inv.code}</span>
              <Badge tone={inv.status === 'open' ? 'accent' : 'neutral'}>
                {t.admin.inviteStatus[inv.status]}
              </Badge>
            </div>
            <p className="text-xs text-hint">
              {creditsCount(inv.credits)}
              {inv.note ? ` · ${inv.note}` : ''}
            </p>
            {inv.usedBy && (
              <p className="text-xs text-hint">{t.admin.inviteUsedBy(personLabel(inv.usedBy))}</p>
            )}
            {inv.status === 'open' && (
              <div className="grid grid-cols-2 gap-2 mt-3">
                <Button variant="secondary" size="sm" disabled={!inv.link} onClick={() => copy(inv)}>
                  {t.common.copy}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(inv.id)}
                >
                  {t.admin.inviteRevoke}
                </Button>
              </div>
            )}
            {inv.status === 'open' && !inv.link && (
              <p className="text-xs text-hint mt-2">{t.admin.inviteNoLink}</p>
            )}
          </Card>
        ))}
      </div>

      {toast && (
        <Toast tone="neutral" placement="fixed">
          {toast}
        </Toast>
      )}
    </div>
  );
}
